import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { dayjs } from "@/core/utils/date";
import {
  UPLOAD_CONFLICT_RESOLUTION,
  type UploadConflict,
  type UploadConflictResolution,
} from "./types";
import { useStepDialog } from "./use-step-dialog";

interface UploadConflictModalProps {
  conflict: UploadConflict | null;
  onCancel: () => void;
  onResolve: (resolution: UploadConflictResolution) => void;
}

export const UploadConflictModal = ({
  conflict,
  onCancel,
  onResolve,
}: UploadConflictModalProps) => {
  const open = useStepDialog(!!conflict, onCancel);
  const description = conflict
    ? `You already sent a file called ${conflict.file_name}. Replace it, or keep both?`
    : "";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>You already sent this file</DialogTitle>
        </DialogHeader>
        {description && (
          <DialogDescription className="sr-only">
            {description}
          </DialogDescription>
        )}

        {conflict && (
          <Stack align="start" className="w-full" gap={4}>
            <Text tone="muted">
              You already sent a file called{" "}
              <span className="font-semibold text-ink">
                {conflict.file_name}
              </span>
              , on{" "}
              {dayjs(conflict.existing_document.uploaded_at).format(
                "MMM D, YYYY",
              )}
              .
            </Text>
            <Text variant="caption" tone="muted">
              Replacing removes the old one for good. Keeping both saves this
              one as {conflict.suggested_file_name}.
            </Text>
          </Stack>
        )}

        <DialogFooter>
          <Button variant="secondary" type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            type="button"
            onClick={() => onResolve(UPLOAD_CONFLICT_RESOLUTION.keepBoth)}
          >
            Keep both
          </Button>
          <Button
            type="button"
            onClick={() => onResolve(UPLOAD_CONFLICT_RESOLUTION.replace)}
          >
            Replace it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
